import { scaleLinear } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import moment from "moment"
import { createSelector } from "reselect"
import { filter, first, last, get, find } from "lodash"
import { calculateHighlighter } from "../helpers/highlighter"

const MARGINS = { top: 2, left: 0, right: 0, bottom: 2 }

const getDate = props => props.date
const getRawData = props => props.data
const getWidth = props => props.width || 150
const getColor = props => props.color
const getHeight = props => props.height

export const getData = createSelector(
  [getRawData],
  data => filter(data, datum => Number.isFinite(datum.value)),
)

const getMinX = createSelector(
  [getRawData],
  (data) => get(first(data), "date"),
)

const getMaxX = createSelector(
  [getRawData],
  (data) => get(last(data), "date"),
)

const getYValues = createSelector(
  [getData],
  (data) => data.map(datum => datum.value),
)

const getMinY = createSelector(
  [getYValues],
  (yValues) => Math.min(...yValues, -2.5),
)

const getMaxY = createSelector(
  [getYValues],
  (yValues) => Math.max(...yValues, 2.5),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleLinear()
    .domain([minX, maxX])
    .rangeRound([0, width - MARGINS.left - MARGINS.right]),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight],
  (minY, maxY, height) => scaleLinear()
    .domain([minY, maxY])
    .rangeRound([height - MARGINS.top, MARGINS.bottom]),
)

export const getLine = createSelector(
  [getScaleX, getScaleY, getData, getColor],
  (scaleX, scaleY, data, color) => ({
    d: line()
      .x(d => scaleX(d.date))
      .y(d => scaleY(d.value))
      .curve(curveLinear)(data),
    transform: `translate(${MARGINS.left},${MARGINS.top - MARGINS.bottom})`,
    style: { stroke: color },
  }),
)

export const getZero = createSelector(
  [getScaleY],
  (scaleY) => scaleY(0),
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getMinY, getMaxY],
  calculateHighlighter,
)

export const getSelected = createSelector(
  [getDate, getData],
  (date, data) => find(data, { date: moment(date).toDate() }),
)
